import { toValue, type MaybeRefOrGetter } from 'vue'
import type { Firestore } from 'firebase/firestore'
import {
  readViewDoc,
  useViewDoc,
  useViewRows,
  type UseViewDocResult,
  type UseViewRowsResult,
  type ViewReadOptions,
  type ViewSubscriptionOptions,
} from '@/composables/useClientViews'

/**
 * The client half of MYAC-1161's authored-documents read-model.
 *
 * The backend projects two sibling row families, both in the platform's standard
 * `views/{view}/rows/{rowId}` shape (NOT the `rows/` + `content/` split the design
 * spec asked for — see `services/authored_documents/projection.py` for why). This
 * composable owns the content address so the three surfaces that read it — the
 * document page, the chat side panel and the library — cannot drift apart.
 */
export const AUTHORED_DOCUMENTS_CONTENT_VIEW = 'authored_documents_content'
export const AUTHORED_DOCUMENTS_INDEX_VIEW = 'authored_documents_index'

/**
 * A content row: the ACTIVE revision only, already rendered to markdown on the write
 * path. `lifecycle_state` is a plain `string` on purpose — the backend types it as a
 * tolerant reader, so a vocabulary value added later must degrade in a deployed
 * browser rather than crash it.
 */
export interface AuthoredDocumentContentRow {
  doc_id: string
  title: string
  lifecycle_state: string
  revision: number
  markdown: string
}

/**
 * Live-subscribe to one document's body. An empty `docId` yields an empty view id,
 * which `useViewDoc` treats as "do not subscribe" — that is the panel's no-document-
 * selected state, and it also keeps a half-built path out of `doc()`.
 */
export function useAuthoredDocument(
  projectId: MaybeRefOrGetter<string | null | undefined>,
  docId: MaybeRefOrGetter<string | null | undefined>,
  options: ViewSubscriptionOptions = {},
): UseViewDocResult<AuthoredDocumentContentRow> {
  return useViewDoc<AuthoredDocumentContentRow>(
    projectId,
    () => {
      const id = String(toValue(docId) ?? '')
      return id ? `${AUTHORED_DOCUMENTS_CONTENT_VIEW}/rows/${id}` : ''
    },
    options,
  )
}

/**
 * One-shot read of one document's body — for the library row's exports (MYAC-1667),
 * where the list holds only index rows and a listener per clicked row would outlive
 * the click by the whole page's life. Null when absent; `options.onError` tells a
 * failed read apart from a missing document.
 */
export function readAuthoredDocument(
  db: Firestore,
  projectId: string,
  docId: string,
  options: ViewReadOptions = {},
): Promise<AuthoredDocumentContentRow | null> {
  return readViewDoc<AuthoredDocumentContentRow>(
    db,
    projectId,
    `${AUTHORED_DOCUMENTS_CONTENT_VIEW}/rows/${docId}`,
    options,
  )
}

/**
 * A library row. Same document as the content row, minus the body — which is the
 * point: `block_count` and `rendered_bytes` let the list show a size without
 * pulling a single document's markdown across the wire.
 */
export interface AuthoredDocumentIndexRow {
  doc_id: string
  title: string
  description: string
  lifecycle_state: string
  revision: number
  block_count: number
  rendered_bytes: number
  created_at?: unknown
  updated_at?: unknown
}

/**
 * Live-subscribe to the project's document index.
 *
 * `options.enabled` is the laziness gate and the caller owns it: the library page
 * subscribes while it is mounted and not before, so opening any other page of the
 * project costs no read.
 */
export function useAuthoredDocuments(
  projectId: MaybeRefOrGetter<string | null | undefined>,
  options: ViewSubscriptionOptions = {},
): UseViewRowsResult<AuthoredDocumentIndexRow> {
  return useViewRows<AuthoredDocumentIndexRow>(
    projectId,
    AUTHORED_DOCUMENTS_INDEX_VIEW,
    [],
    options,
  )
}
